import React, { useState, useEffect } from 'react';
import {
  Wallet,
  Plus,
  Search,
  Building2,
  Calendar,
  DollarSign,
  Tag,
  Receipt,
} from 'lucide-react';
import { ExpenseItem } from '../types/index.ts';

interface ExpensesPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const ExpensesPage: React.FC<ExpensesPageProps> = ({ onNavigate }) => {
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);

  const [form, setForm] = useState({
    projectId: '',
    category: 'Labour',
    amount: '',
    paidTo: '',
    paymentMethod: 'Cash',
    description: '',
  });

  const categories = [
    'Labour',
    'Transport',
    'Fuel',
    'Site Expense',
    'Food',
    'Material',
    'Miscellaneous',
  ];

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const [eRes, pRes] = await Promise.all([
        fetch('/api/expenses'),
        fetch('/api/projects'),
      ]);
      if (eRes.ok) {
        const eData = await eRes.json();
        if (Array.isArray(eData)) setExpenses(eData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
          }
        }
      }
    } catch (err) {
      console.warn('Expenses fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchExpenses();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const filtered = (Array.isArray(expenses) ? expenses : []).filter((e) => {
    return categoryFilter === 'ALL' || e.category === categoryFilter;
  });

  const totalSum = filtered.reduce((sum, e) => sum + Number(e.amount), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Site &amp; Project Expenses
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              Field Cost Accumulator
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Log site labour advances, diesel/petrol, truck rentals, and field petty cash linked directly to project costing.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate('costing')}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-900 border border-blue-200 rounded-lg text-xs font-bold transition-colors"
          >
            <span>Project Costing →</span>
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      {/* Category Filter Pills & Total */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => setCategoryFilter('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              categoryFilter === 'ALL'
                ? 'bg-[#0f2942] text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            All Categories
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                categoryFilter === cat
                  ? 'bg-[#0f2942] text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="text-right shrink-0">
          <span className="text-xs text-slate-400 mr-2">Category Sum:</span>
          <span className="font-bold text-sm text-slate-900">{formatPKR(totalSum)}</span>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Voucher #</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Project</th>
                <th className="py-3 px-4">Paid To / Recipient</th>
                <th className="py-3 px-4">Scope / Description</th>
                <th className="py-3 px-4">Payment Method</th>
                <th className="py-3 px-4 text-right">Amount (PKR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4 font-mono font-bold text-blue-900">{e.expenseNumber}</td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-800">
                      {e.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-slate-800">{e.projectNumber}</td>
                  <td className="py-3 px-4 text-slate-700">{e.paidTo || 'Site Gang'}</td>
                  <td className="py-3 px-4 max-w-sm truncate text-slate-600">{e.description}</td>
                  <td className="py-3 px-4 text-slate-500">{e.paymentMethod}</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatPKR(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Record Project / Site Expense</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Project *</label>
                  <select
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.projectId}
                    onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                  >
                  {(Array.isArray(projects) ? projects : []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Expense Category *</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as any })}
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Amount (Rs.) *</label>
                  <input
                    type="number"
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Payment Method</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.paymentMethod}
                    onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Petty Cash">Petty Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Easypaisa / JazzCash">Easypaisa / JazzCash</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Paid To / Recipient</label>
                <input
                  type="text"
                  placeholder="e.g. Mazda Truck Rental / Tile Labour Ustad"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.paidTo}
                  onChange={(e) => setForm({ ...form, paidTo: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Purpose / Description *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="e.g. Fuel for Hilux, transport of 100 bags cement, overtime labour..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Save &amp; Accumulate to Project Cost
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
